/**
 * Attach a client contact to a generator — create a new one, or link one that
 * already exists.
 *
 * Backed by `fieldService_attachGeneratorContact`
 * (functions/src/field-service/services/generatorContactService.ts), which owns
 * the AppSheet write order and the idempotency lock. This hook is thin on purpose.
 *
 * **No optimistic insert on the create path, deliberately.** AppSheet mints the
 * `Contact ID`, so an optimistic row would have to invent one; the invented id
 * would then be the thing the UI keys off until revalidation replaced it. Every
 * other mutation in this folder patches rows it can already address. Here we
 * invalidate and let the real row land — a contact that appears half a second
 * late is strictly better than one that appears instantly under an id that does
 * not exist.
 *
 * @module hooks/fieldService/useAttachGeneratorContact
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { functions } from '../../config/firebase';
import { dataHubContactKeys } from '../useDataHubContacts';
import { dataHubEquipmentKeys } from '../useDataHubEquipment';
import { generatorKeys } from '../useGenerator';
import { sharedContactKeys } from './useSharedContactMutations';
import { pendingContactKeys } from './usePendingGeneratorContacts';
import toast from 'react-hot-toast';

/** The fields a brand-new contact can carry. All optional — see below. */
export interface NewContactDraft {
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
  phone?: string;
  role?: string;
}

export interface AttachGeneratorContactInput {
  /** Business Generator ID (`G2016-0002`), NOT the Firestore doc id. */
  generatorId: string;
  /** AppSheet `Contact ID` to link. Mutually exclusive with `newContact`. */
  contactId?: string;
  /** Details for a contact to create. Mutually exclusive with `contactId`. */
  newContact?: NewContactDraft;
  /** Also set this contact as the generator's Primary Contact. */
  makePrimary?: boolean;
  /**
   * Stable token for one dialog session. Minted when the dialog OPENS, not per
   * submit — the server locks on it, so a double-click or a retry after a slow
   * response resolves to the same contact instead of creating two.
   */
  idempotencyKey?: string;
}

export interface AttachGeneratorContactResult {
  contactId: string;
  contactDocId: string | null;
  linkedExisting: boolean;
  replayed: boolean;
  isPrimary: boolean;
  /** Fast save: true = saved, AppSheet has not answered yet. A NEW contact's `contactId` is blank until it does. */
  queued?: boolean;
  /** Fast save: the id of the "Saving contact..." row on the unit's list. */
  pendingId?: string;
}

/**
 * Read the existing contact id off an `already-exists` refusal, when the server sent
 * one (the A1 duplicate-contact check, red-team finding on #4932 — see
 * `generatorContactService.assertContactNotDuplicate`).
 *
 * `already-exists` is thrown for TWO different reasons server-side and they need
 * different handling: the idempotency lock's "already being saved" (no details) and
 * A1's "a contact matching these details already exists" (carries
 * `{ existingContactId }` so the UI can offer "attach existing" instead of just
 * apologizing). This is how the two are told apart.
 *
 * @param error The callable failure, typically from `mutation.error`.
 * @return The existing contact's AppSheet `Contact ID`, or null when this was not a
 *   duplicate-contact refusal (or the server sent no id).
 */
export function readDuplicateContactId(error: unknown): string | null {
  const details = (error as { details?: unknown })?.details;
  if (!details || typeof details !== 'object') return null;
  const id = (details as Record<string, unknown>).existingContactId;
  return typeof id === 'string' && id ? id : null;
}

/**
 * Read the server's `contactCreated` discriminator off an `aborted` refusal.
 *
 * `aborted` covers TWO half-writes that need opposite advice, and they deliberately share
 * a code because both are retryable:
 *
 *   - the Add TIMED OUT — genuinely ambiguous, the contact may or may not exist;
 *   - the Add SUCCEEDED and returned a minted `Contact ID`, and the LOCAL mirror write
 *     failed afterwards — the row definitely exists, and a same-key retry heals it.
 *
 * Only the second carries `{ contactCreated: true }`. A details FLAG rather than a
 * substring match on the message: the messages are user-facing copy and get reworded by
 * people who will not be reading this file, whereas the flag is a contract.
 *
 * @param error The callable failure, typically from `mutation.error`.
 * @return true only when the server confirmed the contact row was created.
 */
export function readContactCreated(error: unknown): boolean {
  const details = (error as { details?: unknown })?.details;
  if (!details || typeof details !== 'object') return false;
  return (details as Record<string, unknown>).contactCreated === true;
}

/**
 * True when the server refused only because another change to the same contact holds it for a moment: nothing was saved, retrying
 * is the right move. A flag in the details, never a match on the message.
 */
export function readRetryLater(error: unknown): boolean {
  const details = (error as { details?: unknown })?.details;
  return !!details && typeof details === 'object' && (details as Record<string, unknown>).retryLater === true;
}

/**
 * Turn a callable failure into something a dispatcher can act on.
 *
 * The server's refusals are all recoverable by a human doing something specific,
 * so each one says what. A generic "failed to save" would hide, in particular,
 * the `aborted` case — where the contact may ALREADY exist and retrying is the
 * wrong move.
 */
export function messageFor(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'functions/already-exists': {
        const existingContactId = readDuplicateContactId(error);
        if (existingContactId) {
          return 'A contact matching these details already exists — search for it and attach the existing one instead of creating a new one.';
        }
        return 'This contact is already being saved — give it a moment and refresh.';
      }
      case 'functions/aborted':
        // A THIRD, harmless one: another change to this contact (a removal, a delete, another save) holds the contact for a moment and
        // nothing was saved. Flagged by `retryLater`, never by prose.
        if (readRetryLater(error)) return 'Another change to this contact is still in progress. Try again in a moment.';
        // TWO different situations share this code, and they call for opposite user
        // behaviour. The server flags them apart with `contactCreated` in the details —
        // read the FLAG, never the prose (see `readContactCreated`).
        if (readContactCreated(error)) {
          // The AppSheet row is definitely there: the Add returned a minted Contact ID
          // and only the local mirror write failed afterwards. "May have already saved"
          // would be actively wrong — it sends a dispatcher hunting for a contact that is
          // on file, or re-typing one that exists. Retrying is not just safe but the
          // repair: the create lock still holds the minted id, so the same-key retry
          // heals the mirror instead of minting a second row.
          return 'The contact was created, but we could not finish saving it here. Try again: it will be linked, not duplicated.';
        }
        return 'An earlier attempt may have already saved this contact. Check the unit\'s contacts before trying again.';
      case 'functions/deadline-exceeded':
        // The BROWSER gave up, not the server — which says nothing about whether the
        // write landed. Anything resembling "could not save" is a claim this client is
        // not in a position to make. Retrying really is safe: the server locks on the
        // dialog's idempotency key and replays the same contact rather than minting a
        // second one, so the honest advice is "look first, then retry if you need to".
        return 'This took longer than expected and the contact may have been saved anyway. Check the unit\'s contacts — trying again is safe and will not create a duplicate.';
      case 'functions/invalid-argument':
        return error.message || 'Those contact details could not be saved.';
      case 'functions/permission-denied':
        return 'You do not have access to this generator.';
      case 'functions/not-found':
        // ONE message, deliberately. The server answers not-found identically for a
        // genuine miss, a soft-deleted row and another org's record, so that a caller
        // cannot use it to discover which ids exist elsewhere. Rendering a distinct
        // "you do not have access" string here would hand back exactly the signal the
        // server just took care to withhold.
        return 'That generator or contact could not be found.';
      case 'functions/failed-precondition':
        return error.message || 'Contacts cannot be saved right now.';
      default:
        return 'Could not save the contact.';
    }
  }
  return 'Could not save the contact.';
}

/**
 * Create or link a client contact on a generator.
 *
 * @return A React Query mutation whose `mutateAsync` resolves to the attached contact.
 */
export function useAttachGeneratorContact() {
  const queryClient = useQueryClient();

  return useMutation<AttachGeneratorContactResult, unknown, AttachGeneratorContactInput>({
    // NO CLIENT-SIDE RETRY, deliberately. Note: the app default
    // (`QUERY_CLIENT_DEFAULTS.mutations.retry = false`, 2026-09-28) no longer retries writes;
    // this local `retry: false` is defence in depth. Why it matters here: the OLD default
    // retried a transient failure up to TWO more times, and `NON_RETRYABLE_CODES` contains
    // neither `functions/deadline-exceeded` nor `functions/aborted`. Both of those are exactly
    // what this callable throws, and paired with the 180s timeout below a retry is wrong twice over:
    //
    //   1. THE CLOCK. Three 180s attempts plus 1s + 2s of backoff can hold the Save
    //      button disabled for roughly NINE MINUTES before the dialog says anything.
    //   2. THE MESSAGE. The retries are silent, so the error `onError` finally renders is
    //      the LAST one — and a replay against a server that already minted the contact
    //      comes back `already-exists`. The deadline wording below, written precisely to
    //      tell the user the write may have landed, was unreachable in the one case it
    //      exists for.
    //
    // Nothing is lost by not retrying: the SERVER owns idempotency (it locks on
    // `idempotencyKey` and replays rather than duplicating), and every refusal it throws
    // is written for a human to read and act on. Retrying that is the dialog's job to
    // offer, not this hook's to do behind the user's back.
    retry: false,
    mutationFn: async (input) => {
      const fn = httpsCallable<AttachGeneratorContactInput, AttachGeneratorContactResult>(
        functions,
        'fieldService_attachGeneratorContact',
        // Matches the server's own budget: `ATTACH_GENERATOR_CONTACT_TIMEOUT_SECONDS`
        // (180) in `functions/src/field-service/services/generatorContactService.ts`.
        // It cannot be imported across the client/functions boundary, so it is restated
        // here with its name — the two AppSheet Finds alone are bounded at 35s each, so
        // the SDK's DEFAULT 70s would abandon calls the server is still legitimately
        // working on, turning a slow success into a `deadline-exceeded` the user reads
        // as a failure. Precedent: `useNeedsAttention.ts` (`{ timeout: 300_000 }`).
        { timeout: 180_000 }
      );
      const { data } = await fn(input);
      return data;
    },
    onSuccess: (result) => {
      // `replayed` means the server matched this to an earlier submit and returned
      // that contact rather than making a second one. Say so — a user who meant to
      // add two people needs to notice, not assume both landed.
      if (result.queued) {
        // Fast save: the person is saved and shows on the unit as "Saving contact..." until AppSheet answers.
        toast.success(result.replayed ? 'That contact is already saving' : 'Contact saved — finishing in the background');
      } else if (result.replayed) {
        toast.success('That contact was already added — showing the existing one');
      } else if (result.linkedExisting) {
        toast.success('Contact linked to this unit');
      } else {
        toast.success('Contact added');
      }
    },
    onError: (error) => {
      toast.error(messageFor(error));
    },
    onSettled: () => {
      // ⚠️ The GENERATOR caches are the load-bearing ones here, and an earlier
      // version of this hook invalidated only the two contact caches — which made
      // the whole feature appear not to work.
      //
      // `GeneratorContacts` derives its contact ids from the GENERATOR document's
      // `Contacts` column, and `sharedContactKeys.byIds(idsCsv, orgId)` is keyed on
      // that same csv. So invalidating the contact caches alone just refetches the
      // SAME stale id set: with `staleTime: 5min` and the global
      // `refetchOnWindowFocus: false`, the user saw "Contact added", the panel
      // closed, and the card still read "No one is on file for this unit yet" —
      // precisely the state this feature exists to remove.
      void queryClient.invalidateQueries({ queryKey: generatorKeys.all });
      void queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: sharedContactKeys.all });
      void queryClient.invalidateQueries({ queryKey: dataHubContactKeys.all });
      // Fast save: the "Saving contact..." rows come from here.
      void queryClient.invalidateQueries({ queryKey: pendingContactKeys.all });
    },
  });
}
